import * as Sentry from '@sentry/angular';
import { environment } from '@vaki/shared/util/environment';
import {
  DENY_URLS,
  IGNORE_ERROR_PATTERNS,
  isBotUserAgent
} from './noise-filters';
import { scrubSentryEvent } from './pii-scrubber';
import { errorTrackingRuntimeState } from './error-tracking-runtime-state';

/**
 * Browser-side Sentry bootstrap for vaki-web. Called at the very top of
 * main.ts, BEFORE `bootstrapApplication()` — deliberately not deferred.
 *
 * This app's own feasibility spike (Notion
 * 3bfc94b6-2cc7-81cf-b780-f58ca1259319, De-risk comment 2026-08-27)
 * measured the core-only cost at +26.5 kB gzip (~5.5% of the 477.5 kB
 * baseline) and concluded deferring it isn't worth it: "diferir significa
 * que Sentry aún no existe cuando ocurren los errores más tempranos del
 * bootstrap (los que este issue más quiere ver — precisamente los del
 * incidente UTM/Angular 19)". `browserTracingIntegration` is NOT used —
 * same spike measured it at +43.7 kB gzip total, and CrUX/Search Console
 * already give p75 Core Web Vitals for free (this issue's explicit
 * out-of-scope: "Reemplazar GA4 o el trabajo de Core Web Vitals").
 *
 * Runs at Angular-DI-free module scope, so the Remote Config-backed kill
 * switch and dynamic sample rate (AC8, AC2) — which need an Angular
 * injector — are wired separately, after bootstrap, by
 * `ErrorTrackingRuntimeConfigService` via `Sentry.getClient()?.addEventProcessor(...)`
 * (see error-tracking-runtime-config.service.ts's doc comment for why
 * this two-step wiring exists rather than a single `beforeSend`).
 */
export function initErrorTrackingBrowser(): void {
  if (!environment.sentry.enabled || !environment.sentry.dsn) {
    // No-ops in production today by design — see environment.prod.ts's
    // `sentry.enabled: false`. Also the safe behavior once a DSN exists
    // but is momentarily blank (nothing to send to).
    return;
  }

  Sentry.init({
    dsn: environment.sentry.dsn,
    release: environment.appVersion,
    environment: environment.env,
    // No default PII (email/ip/username) auto-attached to events —
    // belt-and-suspenders alongside the explicit scrubbing in
    // pii-scrubber.ts, which still runs regardless of this flag.
    sendDefaultPii: false,
    // No `sampleRate` init option on purpose — sampling is handled below,
    // reading `errorTrackingRuntimeState` (see that module's doc comment
    // for why: the Remote Config-backed rate can only be wired up once
    // Angular's injector exists, after Sentry.init() has already run).
    ignoreErrors: IGNORE_ERROR_PATTERNS,
    denyUrls: DENY_URLS,
    beforeSend: (event) => {
      if (errorTrackingRuntimeState.killSwitchActive) return null;
      if (Math.random() >= errorTrackingRuntimeState.sampleRate) return null;
      const userAgent =
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
      if (isBotUserAgent(userAgent)) return null;
      return scrubSentryEvent(event);
    },
    integrations: (defaultIntegrations) =>
      defaultIntegrations.filter(
        // Explicit belt-and-suspenders: even though `browserTracingIntegration`
        // is never added, this keeps a future accidental
        // `getDefaultIntegrations` change from silently reintroducing it.
        (integration) => integration.name !== 'BrowserTracing'
      )
  });
}
